import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, Card, StatCard, EmptyState } from "@/components/Shell";
import StatusBadge from "@/components/StatusBadge";
import { Link } from "react-router-dom";
import {
  Package, Warehouse, AlertTriangle, FileText, Truck, RotateCcw, Box,
  Inbox, ClipboardCheck, Send, PlusCircle, IndianRupee,
} from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/dashboard").then(r => setData(r.data)).catch(e => setErr(formatApiError(e)));
  }, []);

  if (err) return <div className="p-6 text-rose-600 text-sm">{err}</div>;
  if (!data) return <div className="p-6 text-sm text-zinc-500">Loading dashboard…</div>;

  if (data.role === "admin") return <AdminDash data={data} />;
  if (data.role === "cluster_head") return <ClusterDash data={data} />;
  if (data.role === "warehouse_manager") return <WarehouseDash data={data} user={user} />;
  return <EngineerDash data={data} user={user} />;
}

function WarehouseDash({ data, user }) {
  const k = data.kpis;
  return (
    <div className="p-4 md:p-8" data-testid="warehouse-dashboard">
      <PageHeader title={`Warehouse Operations — ${user.name}`} subtitle={`Managing ${k.warehouses_managed} warehouse(s).`} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard label="Warehouses Managed" value={k.warehouses_managed} icon={Warehouse} accent="orange" />
        <StatCard label="Total Stock (units)" value={k.total_stock_qty} icon={Package} accent="blue" />
        <StatCard label="Low Stock Items" value={k.low_stock_count} icon={AlertTriangle} accent="red" />
        <StatCard label="Recoveries In-Transit" value={k.in_transit_recoveries} icon={RotateCcw} accent="yellow" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-5">
          <div className="font-display font-semibold mb-4">My Warehouses</div>
          {data.warehouse_stock.length === 0 ? <EmptyState title="No warehouses assigned" icon={Warehouse} /> :
            <div className="space-y-3">
              {data.warehouse_stock.map(w => (
                <div key={w.warehouse_id} className="flex items-center justify-between text-sm">
                  <div className="font-medium">{w.warehouse_name}</div>
                  <div className="num font-bold">{w.quantity}</div>
                </div>
              ))}
            </div>
          }
        </Card>
        <Card className="p-5">
          <div className="font-display font-semibold mb-4">Low Stock</div>
          {data.low_stock.length === 0 ? <EmptyState title="All stocks healthy" icon={Box} /> :
            <div className="space-y-2 max-h-80 overflow-auto">
              {data.low_stock.map((x, i) => (
                <div key={i} className="flex items-center justify-between text-xs border-b border-zinc-100 pb-2">
                  <div>
                    <div className="font-semibold text-sm">{x.product_name}</div>
                    <div className="text-zinc-500">{x.sku}</div>
                  </div>
                  <div className="text-right">
                    <div className="num font-bold text-rose-600">{x.quantity}</div>
                    <div className="text-[10px] text-zinc-400">min {x.min_stock}</div>
                  </div>
                </div>
              ))}
            </div>
          }
        </Card>
        <Card className="p-5">
          <div className="font-display font-semibold mb-4">Recent Movements</div>
          {data.recent_movements.length === 0 ? <EmptyState title="No movements" icon={Send} /> :
            <div className="space-y-2 max-h-80 overflow-auto">
              {data.recent_movements.map((m, i) => (
                <div key={i} className="flex items-center justify-between text-xs border-b border-zinc-100 pb-2">
                  <div>
                    <div className="font-semibold text-sm">{m.product_name}</div>
                    <div className="text-zinc-500">{m.warehouse_name} · <span className="capitalize">{m.kind.replace("_"," ")}</span></div>
                  </div>
                  <div className={`num font-bold ${m.quantity < 0 ? "text-rose-600" : "text-emerald-600"}`}>
                    {m.quantity > 0 ? "+" : ""}{m.quantity}
                  </div>
                </div>
              ))}
            </div>
          }
        </Card>
      </div>
    </div>
  );
}

function AdminDash({ data }) {
  const k = data.kpis;
  return (
    <div className="p-4 md:p-8" data-testid="admin-dashboard">
      <PageHeader
        title="Operations Dashboard"
        subtitle="Live overview of stock, requests, dispatch and recovery activity."
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard label="Stock Value" value={`₹${(k.total_stock_value/100000).toFixed(1)}L`} hint={`${k.total_stock_qty} units`} icon={IndianRupee} accent="orange" />
        <StatCard label="Products" value={k.total_products} hint={`${k.total_warehouses} warehouses`} icon={Package} accent="blue" />
        <StatCard label="Pending Approvals" value={k.pending_approvals} hint="Across cluster + admin" icon={ClipboardCheck} accent="yellow" />
        <StatCard label="Low Stock Items" value={k.low_stock_count} hint="Below min level" icon={AlertTriangle} accent="red" />
        <StatCard label="Usage Updates Pending" value={k.pending_usage_update} hint="From engineers" icon={Inbox} accent="purple" />
        <StatCard label="Dispatches In-Transit" value={k.in_transit_dispatches} icon={Truck} accent="blue" />
        <StatCard label="In-Transit Recovery" value={k.in_transit_recoveries} icon={RotateCcw} accent="yellow" />
        <StatCard label="Faulty/Repair/Scrap" value={`${data.damaged_summary.faulty}/${data.damaged_summary.repair}/${data.damaged_summary.scrap}`} icon={Box} accent="red" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Warehouse stock */}
        <Card className="p-5 lg:col-span-1">
          <div className="flex items-center justify-between mb-4">
            <div className="font-display font-semibold">Warehouse Stock</div>
            <Link to="/stock" className="text-xs font-semibold text-[#F97316] hover:underline">View all</Link>
          </div>
          {data.warehouse_stock.length === 0 ? <EmptyState title="No warehouses" icon={Warehouse} /> :
            <div className="space-y-3">
              {data.warehouse_stock.map(w => (
                <div key={w.warehouse_id} className="flex items-center justify-between text-sm">
                  <div className="font-medium">{w.warehouse_name}</div>
                  <div className="num font-bold">{w.quantity}</div>
                </div>
              ))}
            </div>
          }
        </Card>

        {/* Low stock */}
        <Card className="p-5 lg:col-span-1">
          <div className="flex items-center justify-between mb-4">
            <div className="font-display font-semibold">Low Stock Alerts</div>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-rose-50 text-rose-600 border border-rose-200">{data.low_stock.length}</span>
          </div>
          {data.low_stock.length === 0 ? <EmptyState title="All stocks healthy" icon={Box} /> :
            <div className="space-y-2 max-h-80 overflow-auto">
              {data.low_stock.map((x, i) => (
                <div key={i} className="flex items-center justify-between text-xs border-b border-zinc-100 pb-2">
                  <div>
                    <div className="font-semibold text-sm">{x.product_name}</div>
                    <div className="text-zinc-500">{x.sku}</div>
                  </div>
                  <div className="text-right">
                    <div className="num font-bold text-rose-600">{x.quantity}</div>
                    <div className="text-[10px] text-zinc-400">min {x.min_stock}</div>
                  </div>
                </div>
              ))}
            </div>
          }
        </Card>

        {/* Recent movements */}
        <Card className="p-5 lg:col-span-1">
          <div className="flex items-center justify-between mb-4">
            <div className="font-display font-semibold">Recent Movements</div>
          </div>
          {data.recent_movements.length === 0 ? <EmptyState title="No movements yet" icon={Send} /> :
            <div className="space-y-2 max-h-80 overflow-auto">
              {data.recent_movements.map((m, i) => (
                <div key={i} className="flex items-center justify-between text-xs border-b border-zinc-100 pb-2">
                  <div>
                    <div className="font-semibold text-sm">{m.product_name}</div>
                    <div className="text-zinc-500">{m.warehouse_name} · <span className="capitalize">{m.kind.replace("_"," ")}</span></div>
                  </div>
                  <div className={`num font-bold ${m.quantity < 0 ? "text-rose-600" : "text-emerald-600"}`}>
                    {m.quantity > 0 ? "+" : ""}{m.quantity}
                  </div>
                </div>
              ))}
            </div>
          }
        </Card>
      </div>
    </div>
  );
}

function ClusterDash({ data }) {
  const k = data.kpis;
  return (
    <div className="p-4 md:p-8" data-testid="cluster-dashboard">
      <PageHeader title="Cluster Operations" subtitle="Approve and track your engineers' material activity." />
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <StatCard label="Engineers" value={k.engineers_count} icon={Inbox} accent="blue" />
        <StatCard label="Pending Approvals" value={k.pending_approvals} icon={ClipboardCheck} accent="yellow" />
        <StatCard label="Approved" value={k.approved_count} icon={ClipboardCheck} accent="green" />
        <StatCard label="Rejected" value={k.rejected_count} icon={AlertTriangle} accent="red" />
        <StatCard label="Pending Usage" value={k.pending_usage_update} icon={Inbox} accent="purple" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-5">
          <div className="font-display font-semibold mb-4">Engineers Under You</div>
          {data.engineers.length === 0 ? <EmptyState title="No engineers mapped" /> :
            <div className="space-y-2">
              {data.engineers.map(e => (
                <div key={e.id} className="flex items-center justify-between border-b border-zinc-100 pb-2">
                  <div>
                    <div className="font-semibold text-sm">{e.name}</div>
                    <div className="text-xs text-zinc-500">{e.email}</div>
                  </div>
                </div>
              ))}
            </div>
          }
        </Card>
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="font-display font-semibold">Recent Requests</div>
            <Link to="/requests" className="text-xs font-semibold text-[#F97316] hover:underline">View all</Link>
          </div>
          {data.recent_requests.length === 0 ? <EmptyState title="No requests yet" icon={FileText} /> :
            <div className="space-y-2">
              {data.recent_requests.map(r => (
                <Link key={r.id} to={`/requests/${r.id}`} className="flex items-center justify-between border-b border-zinc-100 pb-2 hover:bg-zinc-50 px-1">
                  <div>
                    <div className="font-semibold text-sm">{r.request_no}</div>
                    <div className="text-xs text-zinc-500">{r.customer_name} · {r.requester_name}</div>
                  </div>
                  <StatusBadge status={r.status} />
                </Link>
              ))}
            </div>
          }
        </Card>
      </div>
    </div>
  );
}

function EngineerDash({ data, user }) {
  const k = data.kpis;
  return (
    <div className="p-4 md:p-8" data-testid="engineer-dashboard">
      <div className="mb-6">
        <div className="text-xs font-bold uppercase tracking-widest text-[#F97316]">Welcome back</div>
        <h1 className="font-display text-3xl font-bold mt-1">{user.name}</h1>
        <p className="text-sm text-zinc-500 mt-2">Raise requests, update material usage, and submit recoveries on the go.</p>
      </div>

      <Link to="/requests/new" data-testid="raise-request-cta" className="mb-6 inline-flex w-full md:w-auto items-center justify-center gap-2 bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold text-sm py-3 px-5 rounded-md">
        <PlusCircle size={16} /> Raise New Material Request
      </Link>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
        <StatCard label="Pending Requests" value={k.pending_requests} icon={ClipboardCheck} accent="yellow" />
        <StatCard label="Approved (Ready)" value={k.approved_requests} icon={Send} accent="blue" />
        <StatCard label="Items Issued" value={k.issued_serial_count} hint="Serialized" icon={Package} accent="green" />
        <StatCard label="Usage Pending" value={k.pending_usage_update} icon={Inbox} accent="red" />
      </div>

      <Card className="p-5 mt-6">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display font-semibold">My Recent Requests</div>
          <Link to="/requests" className="text-xs font-semibold text-[#F97316] hover:underline">View all</Link>
        </div>
        {data.recent_requests.length === 0 ?
          <EmptyState title="No requests yet" desc="Raise your first material request." icon={FileText} /> :
          <div className="space-y-3">
            {data.recent_requests.map(r => (
              <Link key={r.id} to={`/requests/${r.id}`} className="flex items-center justify-between border-b border-zinc-100 pb-3 hover:bg-zinc-50 px-1 rounded">
                <div>
                  <div className="font-semibold text-sm">{r.request_no}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">{r.customer_name || "—"} · {r.priority}</div>
                </div>
                <StatusBadge status={r.status} />
              </Link>
            ))}
          </div>
        }
      </Card>
    </div>
  );
}
